/*
  LAS split shipment (Liesel Anne Studios)
  ----------------------------------------
  Lets a customer send parts of one order to extra addresses from inside the
  Elite Cart drawer, and pay one "Ship to another address" fee per extra address.

  What it touches:
    - cart attributes only: `_las_split` (machine copy) and "Shipment B", "Shipment C"...
      (readable copies that show on the order under Additional details)
    - the quantity of the "Ship to another address" fee line
  What it never touches: any other cart line, its quantity, or its properties (DPO).

  After checkout the order is split into #NNNN-B, #NNNN-C... from `_las_split`.
  Config comes from snippets/las-split-shipment.liquid (window.LAS_SPLIT_CFG).
  Docs: skills/las-shopify-site/references/custom-code.md
*/
(function () {
  'use strict';
  var CFG = window.LAS_SPLIT_CFG;
  if (!CFG || window.__lasSplitLoaded) return;
  window.__lasSplitLoaded = true;

  var ATTR = '_las_split';
  var READABLE_PREFIX = 'Shipment ';
  var PRIMARY = '#213557';
  var STATES = [['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','District of Columbia'],['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming']];

  var busy = false;
  var pending = false;
  var lastCart = null;

  // ---------- helpers ----------
  function money(cents) {
    var amount = (cents / 100).toFixed(2);
    return (CFG.moneyFormat || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, amount).replace(/<[^>]*>/g, '');
  }
  function letter(i) { return String.fromCharCode(66 + i); } // B, C, D...
  function el(tag, attrs, kids) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === 'style') n.style.cssText = attrs[k];
      else if (k === 'text') n.textContent = attrs[k];
      else if (k.slice(0, 2) === 'on') n.addEventListener(k.slice(2), attrs[k]);
      else n.setAttribute(k, attrs[k]);
    });
    (kids || []).forEach(function (c) { if (c) n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return n;
  }
  function api(path, body) {
    // `elitecart=1` marks the request as Elite Cart's own, so Elite Cart does not
    // react to it mid-save; we refresh Elite Cart ourselves when we are done.
    var url = path + (path.indexOf('?') > -1 ? '&' : '?') + 'elitecart=1';
    var opts = body ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }
                    : { headers: { Accept: 'application/json' } };
    return fetch(url, opts).then(function (r) {
      if (!r.ok) throw new Error(path + ' ' + r.status);
      return r.json();
    });
  }
  function getCart() { return api('/cart.js'); }

  function isExtraFee(line) { return line.variant_id === CFG.feeVariantId; }
  function isBaseFee(line) { return !!CFG.baseFeeVariantId && line.variant_id === CFG.baseFeeVariantId; }
  function isFee(line) { return isExtraFee(line) || isBaseFee(line); }
  function qtyWhere(cart, test) { return cart.items.filter(test).reduce(function (s, l) { return s + l.quantity; }, 0); }

  // Shopify counts every cart line toward the free-shipping mark, the fee included, and
  // that can't be switched off. So when the extra-address fee is the only thing lifting
  // an order over the mark, the checkout address pays its own shipping here instead:
  // one "Your checkout address" line at the fee price.
  // Packages the customer pays extra for. Checkout's own shipping pays for one package of
  // ours, so when none of our items are left for the checkout address, one extra address
  // takes its place. Partner items (Printify, Collective) are ignored here: they ship
  // separately and checkout charges their shipping on its own.
  function checkoutAddressHasItems(cart, addrs) {
    var used = {};
    addrs.forEach(function (a) { a.items.forEach(function (it) { used[it.k] = (used[it.k] || 0) + it.q; }); });
    return groups(cart).some(function (g) { return g.quantity > (used[g.key] || 0); });
  }
  function chargedExtras(cart, addrs) {
    var n = addrs.filter(function (a) { return a.items.length; }).length;
    if (!n) return 0;
    return checkoutAddressHasItems(cart, addrs) ? n : n - 1;
  }

  function wantBaseFee(cart, extra) {
    if (!CFG.baseFeeVariantId || !CFG.freeShippingCents || !extra) return 0;
    var fees = cart.items.filter(isFee).reduce(function (s, l) { return s + l.final_line_price; }, 0);
    var items = cart.total_price - fees;
    return items < CFG.freeShippingCents && items + extra * CFG.feePrice >= CFG.freeShippingCents ? 1 : 0;
  }
  function splittable(line) {
    return !isFee(line) && line.requires_shipping && line.vendor === CFG.ownVendor &&
      (CFG.excludeProductIds || []).indexOf(line.product_id) === -1;
  }
  function visibleProps(line) {
    var p = line.properties || {}, out = {};
    Object.keys(p).forEach(function (k) {
      if (k.charAt(0) !== '_' && p[k] !== '' && p[k] != null) out[k] = String(p[k]);
    });
    return out;
  }
  function lineTitle(line) {
    return line.product_title + (line.variant_title && line.variant_title !== 'Default Title' ? ' – ' + line.variant_title : '');
  }
  function propsSummary(line) {
    var p = visibleProps(line), keys = Object.keys(p);
    if (!keys.length) return '';
    return keys.slice(0, 2).map(function (k) { return k + ': ' + p[k]; }).join(' · ').slice(0, 90);
  }

  // Shopify's line `key` changes whenever a line's discount changes (a Gifting tier
  // moving from 10% to 8% re-keys every calendar line), so items are identified by
  // variant + all properties instead, and same-identity lines are counted together.
  function ident(line) {
    var p = line.properties || {};
    var s = Object.keys(p).sort().map(function (k) { return k + '=' + p[k]; }).join('\u0001');
    var h = 5381;
    for (var i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
    return line.variant_id + ':' + h.toString(36);
  }
  function groups(cart) {
    var out = [], byId = {};
    cart.items.forEach(function (l) {
      if (!splittable(l)) return;
      var id = ident(l);
      if (!byId[id]) { byId[id] = { key: id, quantity: 0, line: l }; out.push(byId[id]); }
      byId[id].quantity += l.quantity;
    });
    return out;
  }

  function readState(cart) {
    try {
      var s = JSON.parse((cart.attributes || {})[ATTR] || 'null');
      if (s && Array.isArray(s.addrs)) return s;
    } catch (e) {}
    return { v: 1, feeQty: 0, addrs: [] };
  }

  // Clamp a state to what is really in the cart: drop items no longer there,
  // never allocate more than a line holds, drop addresses left with no items.
  function normalise(state, cart) {
    var lines = {};
    groups(cart).forEach(function (g) { lines[g.key] = g; });
    var used = {};
    state.addrs.forEach(function (a) {
      a.items = (a.items || []).filter(function (it) { return lines[it.k]; }).map(function (it) {
        var g = lines[it.k], l = g.line;
        var left = g.quantity - (used[it.k] || 0);
        var q = Math.max(0, Math.min(parseInt(it.q, 10) || 0, left));
        used[it.k] = (used[it.k] || 0) + q;
        return { k: it.k, q: q, v: l.variant_id, t: lineTitle(l), p: visibleProps(l) };
      }).filter(function (it) { return it.q > 0; });
    });
    state.addrs = state.addrs.filter(function (a) { return a.items.length > 0; });
    return state;
  }

  function addrLine(a) {
    return [a.name, a.address1, a.address2, a.city + ', ' + a.province + ' ' + a.zip].filter(Boolean).join(', ');
  }
  function readable(a) {
    var items = a.items.map(function (it) { return it.q + ' × ' + it.t; }).join('; ');
    return addrLine(a) + ' — ' + items + (a.note ? ' — Gift note: ' + a.note : '');
  }

  // Write state to the cart: attributes first, then the fee line.
  // If the fee write fails, the next reconcile sees a split with no fee and clears it.
  function writeState(state, cart) {
    var attrs = {};
    Object.keys(cart.attributes || {}).forEach(function (k) {
      if (k.indexOf(READABLE_PREFIX) === 0) attrs[k] = '';
    });
    state.feeQty = chargedExtras(cart, state.addrs);
    state.addrs.forEach(function (a, i) { attrs[READABLE_PREFIX + letter(i)] = readable(a); });
    attrs[ATTR] = state.addrs.length ? JSON.stringify(state) : '';

    var extra = state.feeQty;
    var want = {};
    want[CFG.feeVariantId] = extra;
    if (CFG.baseFeeVariantId) want[CFG.baseFeeVariantId] = wantBaseFee(cart, extra);
    return api('/cart/update.js', { attributes: attrs }).then(function () { return syncFees(cart, want); });
  }

  // One line per fee variant holding the wanted quantity (duplicates merged, extras removed).
  function syncFees(cart, want) {
    var updates = {}, adds = [], touched = false;
    Object.keys(want).forEach(function (vid) {
      var lines = cart.items.filter(function (l) { return String(l.variant_id) === vid; });
      var q = want[vid];
      if (!lines.length) { if (q > 0) adds.push({ id: Number(vid), quantity: q }); return; }
      lines.forEach(function (l, i) {
        var target = i === 0 ? q : 0;
        if (l.quantity !== target) { updates[l.key] = target; touched = true; }
      });
    });
    var p = touched ? api('/cart/update.js', { updates: updates }) : Promise.resolve();
    return p.then(function () { return adds.length ? api('/cart/add.js', { items: adds }) : null; });
  }
  function feesWrong(cart, extra) {
    var extraLines = cart.items.filter(isExtraFee), baseLines = cart.items.filter(isBaseFee);
    return extraLines.length > 1 || baseLines.length > 1 ||
      qtyWhere(cart, isExtraFee) !== extra || qtyWhere(cart, isBaseFee) !== wantBaseFee(cart, extra);
  }

  function refreshEliteCart() {
    try { if (window.EliteCart && window.EliteCart.refreshCart) window.EliteCart.refreshCart(); } catch (e) {}
  }

  // Keep the cart consistent after anything changes it (Elite Cart steppers, removals, adds).
  function reconcile() {
    if (busy) { pending = true; return Promise.resolve(); }
    busy = true;
    return getCart().then(function (cart) {
      var state = readState(cart);
      var feeQty = qtyWhere(cart, isExtraFee);
      var before = JSON.stringify(state.addrs) + '|' + state.feeQty;

      if (state.addrs.length && state.feeQty > 0 && feeQty === 0) {
        state.addrs = []; // the customer removed the shipping fee: treat it as cancelling the split
      } else {
        normalise(state, cart);
      }
      var stale = JSON.stringify(state.addrs) + '|' + chargedExtras(cart, state.addrs) !== before;
      var feeWrong = feesWrong(cart, chargedExtras(cart, state.addrs));
      if (!stale && !feeWrong) { lastCart = cart; return false; }
      return writeState(state, cart).then(function () { return true; });
    }).then(function (changed) {
      busy = false;
      if (changed) refreshEliteCart();
      if (pending) { pending = false; return reconcile(); }
    }).catch(function (e) {
      busy = false;
      console.warn('[las-split]', e);
    });
  }

  // ---------- button inside Elite Cart ----------
  function shadow() {
    var root = document.getElementById('elitecart-root');
    return root && root.shadowRoot;
  }
  function renderButton(cart) {
    var sr = shadow();
    if (!sr) return;
    var anchor = sr.querySelector('.ec-mode-standard .ec-checkout-buttons-container');
    var existing = sr.querySelector('#las-split-entry');
    var show = cart && cart.items.some(splittable);
    if (!anchor || !show) { if (existing) existing.remove(); return; }
    var state = readState(cart);
    var n = state.addrs.length;
    var label = n ? 'Shipping to ' + (n + 1) + ' addresses' : 'Sending to more than one address?';
    var action = n ? 'Edit' : 'Split shipment';
    var key = label + action;
    if (existing && existing.getAttribute('data-key') === key && existing.nextElementSibling === anchor) return;
    if (existing) existing.remove();
    var box = el('div', { id: 'las-split-entry', 'data-key': key, style: 'display:flex;align-items:center;justify-content:space-between;gap:10px;margin:0 0 12px;padding:10px 12px;border:1px solid #e3e3ea;border-radius:8px;font-size:14px;line-height:1.3;color:#333;background:#fafafc;' }, [
      el('span', { text: '🎁 ' + label }),
      el('button', { type: 'button', style: 'flex:none;cursor:pointer;background:none;border:0;padding:4px 0;font:inherit;font-weight:600;color:' + PRIMARY + ';text-decoration:underline;', onclick: openPanel, text: action })
    ]);
    anchor.parentNode.insertBefore(box, anchor);
  }

  // ---------- panel ----------
  var overlay = null;
  function closePanel() { if (overlay) { overlay.remove(); overlay = null; document.documentElement.style.overflow = ''; } }

  function openPanel() {
    getCart().then(function (cart) {
      var state = normalise(readState(cart), cart);
      buildPanel(cart, state);
    });
  }

  var CSS = '' +
    '#las-split-overlay{position:fixed;inset:0;z-index:2147483647;background:rgba(20,20,35,.45);display:flex;align-items:flex-end;justify-content:center;font-family:inherit;color:#222}' +
    '@media(min-width:640px){#las-split-overlay{align-items:center}}' +
    '#las-split-overlay *{box-sizing:border-box}' +
    '.lss-panel{background:#fff;width:100%;max-width:560px;max-height:92vh;display:flex;flex-direction:column;border-radius:14px 14px 0 0;overflow:hidden}' +
    '@media(min-width:640px){.lss-panel{border-radius:14px}}' +
    '.lss-head{padding:18px 20px 12px;border-bottom:1px solid #eee;position:relative}' +
    '.lss-head h2{margin:0 0 4px;font-size:20px;line-height:1.2}' +
    '.lss-head p{margin:0;font-size:14px;color:#555;line-height:1.4}' +
    '.lss-x{position:absolute;top:12px;right:12px;background:none;border:0;font-size:26px;line-height:1;cursor:pointer;color:#555;padding:4px 8px}' +
    '.lss-body{padding:14px 20px;overflow-y:auto;-webkit-overflow-scrolling:touch;flex:1}' +
    '.lss-card{border:1px solid #e3e3ea;border-radius:10px;padding:14px;margin-bottom:14px}' +
    '.lss-card h3{margin:0 0 10px;font-size:16px;display:flex;justify-content:space-between;align-items:center}' +
    '.lss-card h3 button{background:none;border:0;color:#a33;font:inherit;font-size:13px;cursor:pointer;text-decoration:underline}' +
    '.lss-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px}' +
    '.lss-grid .full{grid-column:1/-1}' +
    '.lss-card label{display:block;font-size:12px;color:#555}' +
    '.lss-card input,.lss-card select,.lss-card textarea{width:100%;font:inherit;font-size:16px;padding:8px 10px;border:1px solid #ccc;border-radius:6px;margin-top:2px;background:#fff;color:#222}' +
    '.lss-card textarea{min-height:56px;resize:vertical}' +
    '.lss-items{margin-top:12px;border-top:1px dashed #ddd;padding-top:10px}' +
    '.lss-items h4{margin:0 0 6px;font-size:13px;text-transform:uppercase;letter-spacing:.04em;color:#666}' +
    '.lss-item{display:flex;justify-content:space-between;align-items:center;gap:10px;padding:6px 0;font-size:14px}' +
    '.lss-item small{display:block;color:#777;font-size:12px}' +
    '.lss-item select{width:auto;min-width:64px;margin:0}' +
    '.lss-err{color:#a33;font-size:13px;margin-top:8px}' +
    '.lss-add{width:100%;padding:12px;border:1px dashed ' + PRIMARY + ';border-radius:10px;background:#fff;color:' + PRIMARY + ';font:inherit;font-weight:600;cursor:pointer;margin-bottom:14px}' +
    '.lss-note{font-size:13px;color:#555;background:#f6f6fa;border-radius:8px;padding:10px 12px;margin-bottom:14px;line-height:1.4}' +
    '.lss-foot{border-top:1px solid #eee;padding:12px 20px 16px;background:#fff}' +
    '.lss-total{display:flex;justify-content:space-between;font-size:14px;margin-bottom:10px}' +
    '.lss-btns{display:flex;gap:10px}' +
    '.lss-btns button{flex:1;padding:13px;border-radius:8px;font:inherit;font-weight:600;cursor:pointer}' +
    '.lss-save{background:' + PRIMARY + ';color:#fff;border:0}' +
    '.lss-save[disabled]{opacity:.6;cursor:wait}' +
    '.lss-cancel{background:#fff;color:#333;border:1px solid #ccc}' +
    '.lss-remove{display:block;margin:10px auto 0;background:none;border:0;color:#a33;font:inherit;font-size:13px;text-decoration:underline;cursor:pointer}';

  function buildPanel(cart, state) {
    closePanel();
    if (!document.getElementById('las-split-css')) document.head.appendChild(el('style', { id: 'las-split-css', text: CSS }));

    var lines = groups(cart); // {key, quantity, line}
    var others = cart.items.filter(function (l) { return !isFee(l) && !splittable(l) && l.requires_shipping; });
    var draft = JSON.parse(JSON.stringify(state.addrs));
    if (!draft.length) draft.push(blankAddr());

    var body = el('div', { class: 'lss-body' });
    var totalEl = el('span');
    var baseEl = el('div', { style: 'display:none;font-size:12px;color:#666;margin:-6px 0 10px;line-height:1.35', text: 'Your items are under ' + money(CFG.freeShippingCents || 0) + ', so your checkout address pays its own shipping too (the extra-address shipping doesn’t count toward free shipping).' });
    var mineEl = el('div', { class: 'lss-note' });
    var errEl = el('div', { class: 'lss-err', role: 'alert' });
    var saveBtn = el('button', { type: 'button', class: 'lss-save', text: 'Save addresses', onclick: save });

    overlay = el('div', { id: 'las-split-overlay', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Split shipment' }, [
      el('div', { class: 'lss-panel' }, [
        el('div', { class: 'lss-head' }, [
          el('h2', { text: 'Send to more than one address' }),
          el('p', { text: 'Add each extra address and choose what goes there. Each extra address is ' + money(CFG.feePrice) + ' shipping. Anything you don’t assign ships to the address you enter at checkout.' }),
          el('button', { type: 'button', class: 'lss-x', 'aria-label': 'Close', text: '×', onclick: closePanel })
        ]),
        body,
        el('div', { class: 'lss-foot' }, [
          el('div', { class: 'lss-total' }, [el('span', { text: 'Extra shipping' }), totalEl]),
          baseEl,
          errEl,
          el('div', { class: 'lss-btns' }, [el('button', { type: 'button', class: 'lss-cancel', text: 'Cancel', onclick: closePanel }), saveBtn]),
          state.addrs.length ? el('button', { type: 'button', class: 'lss-remove', text: 'Remove split — ship everything to one address', onclick: removeAll }) : null
        ])
      ])
    ]);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) closePanel(); });
    // Keep keystrokes and clicks inside the panel so nothing behind it (Elite Cart's drawer) reacts.
    ['keydown', 'keyup', 'keypress', 'focusin', 'click', 'mousedown', 'pointerdown', 'touchstart'].forEach(function (t) {
      overlay.addEventListener(t, function (e) { if (e.key === 'Escape') closePanel(); e.stopPropagation(); });
    });
    document.body.appendChild(overlay);
    document.documentElement.style.overflow = 'hidden';

    function blankAddr() { return { name: '', address1: '', address2: '', city: '', province: '', zip: '', note: '', items: [] }; }
    function qtyOf(a, key) { var it = a.items.filter(function (x) { return x.k === key; })[0]; return it ? it.q : 0; }
    function usedElsewhere(key, except) {
      return draft.reduce(function (s, a, i) { return i === except ? s : s + qtyOf(a, key); }, 0);
    }
    function setQty(a, key, q) {
      a.items = a.items.filter(function (x) { return x.k !== key; });
      if (q > 0) a.items.push({ k: key, q: q });
    }

    var cards = [];
    function renderAll() {
      body.innerHTML = '';
      cards = [];
      draft.forEach(function (a, i) { var c = card(a, i); cards.push(c); body.appendChild(c.node); });
      if (draft.length < (CFG.maxAddresses || 15)) {
        body.appendChild(el('button', { type: 'button', class: 'lss-add', text: '+ Add another address', onclick: function () { draft.push(blankAddr()); renderAll(); var last = cards[cards.length - 1]; if (last) last.first.focus(); } }));
      }
      body.appendChild(mineEl);
      if (others.length) {
        body.appendChild(el('div', { class: 'lss-note', text: 'These are made and shipped by our partners, so they go to your checkout address: ' + others.map(lineTitle).join('; ') + '.' }));
      }
      refreshItems();
    }

    function field(a, key, label, opts) {
      opts = opts || {};
      var input;
      if (opts.states) {
        input = el('select', { autocomplete: 'address-level1' }, [el('option', { value: '', text: 'State' })].concat(STATES.map(function (s) { return el('option', { value: s[0], text: s[1] }); })));
      } else if (opts.textarea) {
        input = el('textarea', { maxlength: '250' });
      } else {
        input = el('input', { type: 'text', autocomplete: opts.ac || 'off', inputmode: opts.inputmode || 'text', maxlength: opts.max || '100' });
      }
      input.value = a[key] || '';
      input.addEventListener('input', function () { a[key] = input.value.trim(); });
      input.addEventListener('change', function () { a[key] = input.value.trim(); });
      return { node: el('label', { class: opts.full ? 'full' : '' }, [label, input]), input: input };
    }

    function card(a, i) {
      var name = field(a, 'name', 'Recipient name', { full: true, ac: 'name' });
      var items = el('div', { class: 'lss-items' });
      var node = el('div', { class: 'lss-card' }, [
        el('h3', {}, [
          el('span', { text: 'Address ' + (i + 2) }),
          draft.length > 1 || state.addrs.length ? el('button', { type: 'button', text: 'Remove', onclick: function () { draft.splice(i, 1); if (!draft.length) draft.push(blankAddr()); renderAll(); } }) : null
        ]),
        el('div', { class: 'lss-grid' }, [
          name.node,
          field(a, 'address1', 'Street address', { full: true, ac: 'address-line1' }).node,
          field(a, 'address2', 'Apt, suite (optional)', { full: true, ac: 'address-line2' }).node,
          field(a, 'city', 'City', { full: true, ac: 'address-level2' }).node,
          field(a, 'province', 'State', { states: true }).node,
          field(a, 'zip', 'ZIP code', { ac: 'postal-code', inputmode: 'numeric', max: '10' }).node,
          field(a, 'note', 'Gift note (optional)', { full: true, textarea: true }).node
        ]),
        items
      ]);
      return { node: node, items: items, first: name.input, a: a, i: i };
    }

    function refreshItems() {
      cards.forEach(function (c) {
        c.items.innerHTML = '';
        c.items.appendChild(el('h4', { text: 'What goes to this address?' }));
        lines.forEach(function (l) {
          var max = l.quantity - usedElsewhere(l.key, c.i);
          var cur = Math.min(qtyOf(c.a, l.key), max);
          var sel = el('select', { 'aria-label': 'How many ' + lineTitle(l.line) });
          for (var q = 0; q <= max; q++) sel.appendChild(el('option', { value: String(q), text: String(q) }));
          sel.value = String(cur);
          sel.addEventListener('change', function () { setQty(c.a, l.key, parseInt(sel.value, 10) || 0); refreshItems(); });
          var sum = propsSummary(l.line);
          c.items.appendChild(el('div', { class: 'lss-item' }, [
            el('div', {}, [lineTitle(l.line), sum ? el('small', { text: sum }) : null, el('small', { text: l.quantity + ' in your cart' })]),
            sel
          ]));
        });
      });
      // What is left for the checkout address.
      var left = lines.map(function (l) { return { l: l, q: l.quantity - usedElsewhere(l.key, -1) }; }).filter(function (x) { return x.q > 0; });
      mineEl.textContent = left.length
        ? 'Shipping to your checkout address: ' + left.map(function (x) { return x.q + ' × ' + lineTitle(x.l.line); }).join('; ') + '.'
        : 'Nothing is left for your checkout address, so the shipping you choose at checkout covers your first address above.';
      var n = chargedExtras(cart, draft);
      var base = wantBaseFee(cart, n);
      var withItems = draft.filter(function (a) { return a.items.length; }).length;
      totalEl.textContent = !n && withItems ? 'Covered by your checkout shipping'
        : n + ' × ' + money(CFG.feePrice) + (base ? ' + ' + money(CFG.feePrice) + ' for your address' : '') + ' = ' + money((n + base) * CFG.feePrice);
      baseEl.style.display = base ? '' : 'none';
    }

    function validate() {
      var problems = [];
      draft.forEach(function (a, i) {
        var used = a.items.length > 0;
        var blank = !a.name && !a.address1 && !a.city && !a.zip && !used;
        if (blank && draft.length === 1) return;
        if (blank) { problems.push('Address ' + (i + 2) + ' is empty — fill it in or remove it.'); return; }
        var miss = [];
        if (!a.name) miss.push('name');
        if (!a.address1) miss.push('street');
        if (!a.city) miss.push('city');
        if (!a.province) miss.push('state');
        if (!/^\d{5}(-\d{4})?$/.test(a.zip || '')) miss.push('5-digit ZIP');
        if (!used) miss.push('at least one item');
        if (miss.length) problems.push('Address ' + (i + 2) + ' needs ' + miss.join(', ') + '.');
      });
      return problems;
    }

    function save() {
      var problems = validate();
      if (problems.length) { errEl.textContent = problems.join(' '); return; }
      errEl.textContent = '';
      var keep = draft.filter(function (a) { return a.items.length; });
      commit(keep);
    }
    function removeAll() { commit([]); }

    function commit(addrs) {
      saveBtn.disabled = true;
      saveBtn.textContent = 'Saving…';
      busy = true;
      getCart().then(function (fresh) {
        var next = normalise({ v: 1, feeQty: 0, addrs: addrs.map(function (a) {
          return { name: a.name, address1: a.address1, address2: a.address2, city: a.city, province: a.province, zip: a.zip, country: 'US', note: a.note, items: a.items };
        }) }, fresh);
        return writeState(next, fresh);
      }).then(function () {
        busy = false;
        closePanel();
        refreshEliteCart();
        return reconcile();
      }).catch(function (e) {
        busy = false;
        console.warn('[las-split]', e);
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save addresses';
        errEl.textContent = 'Something went wrong saving your addresses. Please try again.';
      });
    }

    renderAll();
  }

  // ---------- wiring ----------
  var reconcileTimer = null;
  function scheduleReconcile() {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcile, 350);
  }
  function currentCart() {
    try { return (window.EliteCart && window.EliteCart.getCurrentCart && window.EliteCart.getCurrentCart()) || lastCart; } catch (e) { return lastCart; }
  }

  var observed = null;
  function watchShadow() {
    var sr = shadow();
    if (!sr || observed === sr) return;
    observed = sr;
    var t = null;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(function () { renderButton(currentCart()); }, 60);
    }).observe(sr, { childList: true, subtree: true });
  }

  function hookEliteCart() {
    if (!window.EliteCart || !window.EliteCart.onCartChange) return false;
    window.EliteCart.onCartChange(function (cart) {
      if (cart) lastCart = cart;
      renderButton(cart);
      scheduleReconcile();
    });
    return true;
  }

  document.addEventListener('eliteCartRendered', function (e) {
    watchShadow();
    renderButton((e.detail && e.detail.cart) || currentCart());
  });
  document.addEventListener('eliteCartUpdated', scheduleReconcile);

  var tries = 0;
  (function boot() {
    watchShadow();
    if (hookEliteCart() || ++tries > 40) {
      getCart().then(function (c) { lastCart = c; renderButton(c); }).catch(function () {});
      reconcile();
      return;
    }
    setTimeout(boot, 250);
  })();
})();
